import { useState, useEffect, useCallback } from 'react';
import { getUnitVideoChannels } from '../../services/videoChannelService';

interface UseChannelNamesParams {
  unitId?: number;
}

interface UseChannelNamesReturn {
  availableChannels: number[];
  getChannelName: (channel: number) => string;
  isLoading: boolean;
  channelNames: Map<number, string>;
  videoStreamingChannels: number;
}

/**
 * Hook ligero para obtener solo los nombres de canales sin funcionalidad de edición
 * Útil para componentes que solo necesitan mostrar nombres pero no editarlos
 */
export const useChannelNames = ({
  unitId
}: UseChannelNamesParams): UseChannelNamesReturn => {
  const [availableChannels, setAvailableChannels] = useState<number[]>([]);
  const [channelNames, setChannelNames] = useState<Map<number, string>>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [videoStreamingChannels, setVideoStreamingChannels] = useState<number>(0);

  // Cargar canales disponibles y nombres desde la base de datos
  const loadChannelNames = useCallback(async () => {
    if (!unitId) {
      setAvailableChannels([]);
      setChannelNames(new Map());
      setVideoStreamingChannels(0);
      return;
    }

    setIsLoading(true);
    try {
      const existingChannels = await getUnitVideoChannels(unitId);

      // Determinar canales disponibles basándose en VideoStreamingChannels del dispositivo
      let channels: number[] = [];
      let deviceVideoChannels = 0;

      // Filtrar canales habilitados con channelId válido
      const actualChannels = existingChannels.filter(
        (channel) =>
          channel.channelId !== null && channel.channelId !== undefined && channel.enable !== false
      );

      if (existingChannels.length > 0 && existingChannels[0].videoStreamingChannels) {
        // Usar VideoStreamingChannels del modelo del dispositivo
        deviceVideoChannels = existingChannels[0].videoStreamingChannels;
        channels = actualChannels.map((ch) => ch.channelId as number);
        console.log('✅ Using VideoStreamingChannels from device:', deviceVideoChannels);
        console.log('📺 Available channels (enabled):', channels);
      } else {
        // Fallback al método anterior
        console.log('⚠️ No VideoStreamingChannels info, using fallback');
        channels = [1, 2, 3, 4, 5];
        deviceVideoChannels = 5;
      }

      setAvailableChannels(channels);
      setVideoStreamingChannels(deviceVideoChannels);

      const nameMap = new Map<number, string>();

      // Crear mapa con nombres de canales habilitados
      actualChannels.forEach((channel) => {
        nameMap.set(channel.channelId as number, channel.channelName as string);
      });

      // Para canales sin nombre personalizado, usar nombre por defecto
      channels.forEach((channelNum) => {
        if (!nameMap.has(channelNum)) {
          nameMap.set(channelNum, `Canal ${channelNum}`);
        }
      });

      setChannelNames(nameMap);
    } catch (error) {
      console.error('❌ Error loading channel names:', error);
      // Fallback completo en caso de error
      const fallbackChannels = [1, 2, 3, 4, 5];
      setAvailableChannels(fallbackChannels);
      setVideoStreamingChannels(5);
      
      const defaultMap = new Map<number, string>();
      fallbackChannels.forEach((channelNum) => {
        defaultMap.set(channelNum, `Canal ${channelNum}`);
      });
      setChannelNames(defaultMap);
    } finally {
      setIsLoading(false);
    }
  }, [unitId]);

  // Función para obtener nombre de canal
  const getChannelName = useCallback(
    (channel: number): string => {
      return channelNames.get(channel) || `Canal ${channel}`;
    },
    [channelNames]
  );

  // Cargar al montar o cuando cambien las dependencias
  useEffect(() => {
    loadChannelNames();
  }, [loadChannelNames]);

  return {
    availableChannels,
    getChannelName,
    isLoading,
    channelNames,
    videoStreamingChannels
  };
};
